import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// No mandar más seguido que esto, sin importar cuánto se movió (batería/datos).
const MIN_SEND_INTERVAL_MS = 30000;
// Si no se movió lo suficiente, igual mandamos un "late vivo" cada tanto para
// que el mapa en vivo no marque "sin señal" a alguien parado en un punto.
const HEARTBEAT_MS = 90000;
// Umbral de movimiento para no mandar posiciones repetidas sin necesidad.
const MIN_MOVE_METERS = 20;

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Seguimiento GPS en tiempo real durante una ronda EN CURSO, con la app
 * abierta. No rastrea nada fuera de la ronda ni con la app cerrada (no es
 * posible en web). Si no hay permiso de GPS, la ronda sigue funcionando
 * igual (el escaneo de cada QR valida la posición de forma independiente).
 *
 * @param {string} patrolId
 * @param {boolean} isActive - true solo mientras la ronda está "en_curso"
 * @returns {"idle"|"tracking"|"denied"|"unsupported"} status
 */
export function usePatrolTracking(patrolId, isActive) {
  const [status, setStatus] = useState("idle");
  const watchIdRef = useRef(null);
  const lastSentRef = useRef({ at: 0, lat: null, lon: null });

  useEffect(() => {
    if (!isActive || !patrolId) {
      setStatus("idle");
      return;
    }
    if (!navigator.geolocation) {
      setStatus("unsupported");
      return;
    }

    const send = (lat, lon, accuracy) => {
      api.post("/patrol-tracks", { patrol_id: patrolId, latitude: lat, longitude: lon, accuracy }).catch(() => {
        /* silencioso: el seguimiento nunca debe bloquear la ronda */
      });
    };

    const onPosition = (pos) => {
      setStatus("tracking");
      const { latitude, longitude, accuracy } = pos.coords;
      const now = Date.now();
      const last = lastSentRef.current;
      if (now - last.at < MIN_SEND_INTERVAL_MS) return;
      const moved = last.lat == null ? Infinity : haversineMeters(last.lat, last.lon, latitude, longitude);
      if (last.at && moved < MIN_MOVE_METERS && now - last.at < HEARTBEAT_MS) return;
      lastSentRef.current = { at: now, lat: latitude, lon: longitude };
      send(latitude, longitude, accuracy);
    };

    const onError = () => setStatus("denied");

    watchIdRef.current = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: false,
      maximumAge: 20000,
      timeout: 15000,
    });

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      lastSentRef.current = { at: 0, lat: null, lon: null };
    };
  }, [patrolId, isActive]);

  return status;
}
